import {
  ChangeDetectorRef,
  Directive,
  inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { distinctUntilChanged, of, ReplaySubject, startWith, Subject, switchMap, takeUntil } from 'rxjs';

import { AppUserHasPermissionsInput } from '../auth.model';
import { AuthorizationService } from '../authorization.service';

// Todo: Remove this from here, the core folder should not contain components, directives or pipes

/**
 * appUserHasPermissions checks if the user has any or all of the given permissions (based on the given 'mode' setting).
 * If the user has none of the given permissions, the DOM element gets destroyed.
 *
 * @example
 *
 * Just one permission (the user must have all permissions):
 * <div *appUserHasPermissions="{permissions: 'PERM_A', mode: 'all'}"></div>
 *
 * Multiple permissions (the user must have at least one of permissions):
 * <div *appUserHasPermissions="{permissions: ['PERM_A', 'PERM_B'], mode: 'any'}"></div>
 *
 * Permissions and permission check options:
 * <div *appUserHasPermissions="{permissions: ['PERM_A'], mode: 'all', options: {checkIfFeatureFlagEnabled: 'myFeatureFlag'}}"></div>
 *
 * No permission checks gets performed:
 * <div *appUserHasPermissions="null"></div>
 *
 */
@Directive({
  selector: '[appUserHasPermissions]',
  standalone: true,
})
export class UserHasPermissionsDirective implements OnInit, OnDestroy {
  templateRef = inject(TemplateRef<unknown>);

  viewContainerRef = inject(ViewContainerRef);

  changeDetectorRef = inject(ChangeDetectorRef);

  private appUserHasPermissions$ = new ReplaySubject<AppUserHasPermissionsInput | null | undefined>(1);

  @Input() set appUserHasPermissions(val: AppUserHasPermissionsInput | null | undefined) {
    this.appUserHasPermissions$.next(val);
  }

  private authorizationService = inject(AuthorizationService);

  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.appUserHasPermissions$
      .pipe(
        switchMap(settings => {
          if (settings == null) {
            return of(true);
          }
          if (settings.mode === 'all') {
            return this.authorizationService.userHasAllPermissions(settings.permissions, settings.options);
          }
          return this.authorizationService.userHasAnyPermission(settings.permissions, settings.options);
        }),
        startWith(false),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe(authorized => {
        if (authorized) {
          this.viewContainerRef.createEmbeddedView(this.templateRef);
          this.changeDetectorRef.markForCheck();
          return;
        }
        this.viewContainerRef.clear();
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
